/// <amd-module name="Repos/Views/FilesHub/FilesHubViewService" />
import { IAnnotationBlock, IAnnotationHistoryEntry } from "Repos/Common/Contracts/Editor";
import {
    FileContentMetadata,
    GitCommitRef,
    GitItemRequestData,
    GitObjectType,
    GitQueryCommitsCriteria,
    GitRepository,
    GitVersionDescriptor,
    GitVersionOptions,
    GitVersionType,
    VersionControlRecursionType
} from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import { getGitResourceUri } from "Repos/Common/Util/Editor";
import { getFileExtension, getFileName, getIconNameForFile, getParentPath, trimLeadingSlash, trimTrailingSlash } from "Repos/Common/Util/File";
import { branchNameToRef, getShortCommitId } from "Repos/Common/Util/Ref";
import { commitToVersionDescriptor, getVersionString, gitVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { getGitRepository, IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { MarkdownFilesHubUrlTransformer } from "Repos/Files/Components/ContentPreview";
import { IFilesHubUrlTransformer, IFolderContents, IGitItem, IGitItemContent } from "Repos/Files/Contracts";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { IAnnotateBatchResult, loadAnnotationBlocks } from "Repos/FilesHubCommon/Annotate";
import { IFilesHubCommonService } from "Repos/FilesHubCommon/Contracts";
import { FileEditContext } from "Repos/FilesHubCommon/FileEditContext";
import { getItemCommonMenuItems } from "Repos/FilesHubCommon/FilesHubMenuItems";
import { FilesHubTabsServiceBase, IFilesHubTabRegistration, IFilesHubTabsService } from "Repos/FilesHubCommon/FilesHubTabs";
import * as Resources from "Repos/Views/FilesHub/Resources";
import { getTFSPageData } from "Tfs/Platform/Page";
import {
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    IReadyableReadonlyObservableArray,
    ObservableArray,
    ObservableCollection,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { IVssPageContext, IVssService, Services, setRemoteSerializationSettings, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssPageService } from "VSS/Platform/Page";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { combineUrlPaths, IQueryParameter, toQueryString, Uri } from "VSS/Platform/Util/Url";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { getGitClient } from "./RestClient/Git";

/**
 * Options to get an item URL.
 */
export interface ItemUrlOptions {
    /**
     * Version of the item to include in the URL. By default, it will use current version.
     */
    version?: string;

    /**
     * Whether the URL should be absolute or relative.
     */
    absoluteUrl?: boolean;

    /**
     * Id of the files hub tab to navigate to. If not provided, it will use default tab on page loading.
     */
    tabId?: string;

    /**
     * The anchor to display when navigating to the URL, if the target item has preview (Markdown).
     */
    anchor?: string;
}

/**
 * Service for getting view data for the FilesHub view
 */
export interface IFilesHubViewService extends IVssService, IFilesHubCommonService {
    /**
     * Gets the diff orientation (side-by-side or inline) to use in the Files hub
     */
    getDiffOrientation(): IReadonlyObservableValue<DiffViewerOrientation>;

    /**
     * Gets the menu item to use for toggling diff orientation
     */
    getDiffOrientationMenuItem(): IHeaderCommandBarItem;

    /**
     * Gets the url to download the specified file at the specified version
     *
     * @param path Full path of the file
     * @param version Version at which to get the file
     * @param forceDownload If true, get the URL which forces a browser download/save operation. If false, the URL is one that the browser can "view" -- it can be used as an img src, for example.
     */
    getFileDownloadUrl(path: string, version?: GitVersionDescriptor, forceDownload?: boolean): string;

    /**
     * Get the position at which the editor should scroll to when first displayed
     */
    getScrollToPosition(): { scrollLeft: number; scrollTop: number } | undefined;

    /**
     * Gets the annotation blocks for the currently selected file in the Files hub
     */
    getSelectedItemAnnotationBlocks(): IReadyableReadonlyObservableArray<IAnnotationBlock>;

    /**
     * Gets the content information for the specified file
     *
     * @param path File path
     * @param version version of the file
     */
    getItemContent(path: string, version?: GitVersionDescriptor): IReadonlyObservableValue<IGitItemContent | undefined>;

    /**
     * Gets the currently selected git version descriptor
     */
    getVersion(): GitVersionDescriptor;

    /**
     * Sets the diff orientation (side-by-side or inline) to use in the Files hub
     */
    setDiffOrientation(orientation: DiffViewerOrientation): void;

    /**
     * Sets the position at which the editor should scroll to when first displayed
     */
    setScrollToPosition(position: { scrollLeft: number; scrollTop: number } | undefined): void;
}

/**
 * Data provider for the Files tree and folder list data
 */
const fileListingDataProviderId = "ms.vss-code-web.source-explorer-tree-file-listing-data-provider";
interface IInternalItemDescriptor {
    path: string;
    isFolder: boolean;
    commitId?: string;
    isInvalid?: boolean;
    isNewItem?: boolean;
    isSymLink?: boolean;
    objectId?: string;
}
interface IFileListingDataProviderData {
    descriptor: {
        version: string;
        versionOptions: GitVersionOptions;
        versionType: GitVersionType;
    };
    items: {
        items: {
            [path: string]: IInternalItemDescriptor[];
        };
    };
}

/**
 * Async data provider for getting the latest-commit information for each file in a folder
 */
const lastChangeDataProviderId = "ms.vss-code-web.git-last-change-tree-items-data-provider";
interface ILastChangeDataProviderData {
    commits: GitCommitRef[];
    items: { path: string; commitId: string }[];
}

const annotateGitDiffsDataProviderId = "ms.vss-code-web.annotate-git-diffs-data-provider";

/**
 * Context data passed to third-party menu contributions in the Files hub
 */
interface IFilesHubContributedMenuContext {
    gitRepository: GitRepository;
    item: {
        sourceProvider: string;
        path: string;
        isFolder: boolean;
        isSymLink: boolean;
        item: {
            isFolder: boolean;
            serverItem: string;
            version: string;
            gitObjectType: GitObjectType;
            commitId?: {
                full: string;
                short: string;
            };
            objectId?: {
                full: string;
                short: string;
            };
        };
        url: string;
    };
    version: string;
    getSourceItemContext: () => IFilesHubContributedMenuContext;
}

const explorerModeSettingsKey = "Repos.FilesHub.ExplorerPane";

class FilesHubViewService extends VssService implements IFilesHubViewService {
    private projectId: string;
    private projectName: string;
    private repository: GitRepository;
    private version: GitVersionDescriptor;
    private versionString: string;
    private refName: string | undefined;

    private itemsByPath: { [path: string]: IGitItem };
    private itemsByPathLowercase: { [path: string]: IGitItem };
    private itemsByParentPath: { [parentPath: string]: ReadyableObservableArray<IGitItem> };
    private loadedFolders: ObservableArray<IFolderContents>;
    private foldersWithLastCommits: { [parentPath: string]: boolean };
    private allFilePaths: ReadyableObservableArray<IGitItem>;
    private scrollToPosition: { scrollLeft: number; scrollTop: number } | undefined;

    private selectedItem: ObservableValue<IGitItem>;
    private selectedItemContent: ObservableValue<IGitItemContent | undefined> | undefined;
    private selectedItemAnnotationBlocks: ReadyableObservableArray<IAnnotationBlock> | undefined;
    private editMode: ObservableValue<boolean>;
    private diffOrientation: ObservableValue<DiffViewerOrientation>;
    private explorerCollapsed: ObservableValue<boolean>;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.itemsByPath = {};
        this.itemsByPathLowercase = {};
        this.itemsByParentPath = {};
        this.loadedFolders = new ObservableArray();
        this.foldersWithLastCommits = {};

        this.explorerCollapsed = new ObservableValue<boolean>(tryGetLocalStorageValue(explorerModeSettingsKey) === "collapsed");

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        this.repository = vcViewModelDataProviderService.getRepository()!;

        const userPreferences = vcViewModelDataProviderService.getUserPreferences();
        this.diffOrientation = new ObservableValue(userPreferences.diffViewerOrientation || DiffViewerOrientation.SideBySide);

        const project = getTFSPageData(this.pageContext)!.project!;
        this.projectId = project.id;
        this.projectName = project.name;

        const historyState = pageStateHistoryService.getProjectedState();
        const path = this.fixPath(historyState["path"] || "/");
        const version = historyState["version"];
        const createIfNew = historyState["createIfNew"] === "true";
        if (createIfNew) {
            pageStateHistoryService.queueValueChange("createIfNew", "", HistoryBehavior.replace);
        }

        // Process the version info, files and folders relevant for the initial path
        const fileListingData = contributionService.getData<IFileListingDataProviderData>(fileListingDataProviderId);
        const defaultGitBranchName = vcViewModelDataProviderService.getDefaultGitBranchName() || "";
        if (!fileListingData) {
            this.version = gitVersionStringToVersionDescriptor(version || "GB" + defaultGitBranchName);
            this.versionString = getVersionString(this.version);

            // Start fetching the child items for this path so that we can draw as much of the existing path parts as possible
            this.getChildItems(path, false, createIfNew);
        } else {
            this.version = {
                version: fileListingData.descriptor.version,
                versionOptions: fileListingData.descriptor.versionOptions,
                versionType: fileListingData.descriptor.versionType
            };
            this.versionString = getVersionString(this.version);
            this.processItems(fileListingData.items.items);
        }

        if (this.version.versionType === GitVersionType.Branch) {
            this.refName = "refs/heads/" + this.version.version;
        }

        let selectedItem = this.itemsByPath[path];
        if (!selectedItem) {
            // Item does not exist. Create a entry for the invalid item
            selectedItem = this.createGitItem({ path: path, isFolder: false, isInvalid: true });
        }
        if (selectedItem.isInvalid && createIfNew) {
            selectedItem.isNewItem = true;
            this.itemsByPath[selectedItem.path] = selectedItem;
        }

        this.selectedItem = new ObservableValue(selectedItem);
        this.editMode = new ObservableValue(!!selectedItem.isNewItem);

        pageStateHistoryService.registerObservable<IGitItem>(
            this.selectedItem,
            "path",
            HistoryBehavior.newEntry,
            item => (item.path === "/" ? "" : item.path),
            path => {
                // Selected item path changed from the URL. Ensure that
                // we fully process the change by calling setSelectedItem.
                path = this.fixPath(path);
                const item = this.getItem(path, true);
                this.setSelectedItem(item);
                return item;
            }
        );

        this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").register({
            isDirty: () => {
                return !!(
                    this.editMode.value &&
                    this.selectedItemContent &&
                    this.selectedItemContent.value &&
                    this.selectedItemContent.value.editContext &&
                    (this.selectedItemContent.value.editContext.getIsDirty().value || this.selectedItem.value.isNewItem)
                );
            },
            getDirtyMessage: () => {
                return format(Resources.UnsavedFileFormat, this.selectedItem.value.path);
            }
        });

        // Remember this branch as the default branch to use for this user.
        if (this.version.versionType === GitVersionType.Branch && defaultGitBranchName !== this.version.version) {
            vcViewModelDataProviderService.setUserDefaultBranch(this.version.version);
        }
    }

    public dropFiles(folderItem: IGitItem, dataTransfer: DataTransfer): void {
        this.executeFileActionCommand("upload", folderItem, dataTransfer.files);
    }

    public getDiffOrientation(): IReadonlyObservableValue<DiffViewerOrientation> {
        return this.diffOrientation;
    }

    public getEditMode(): IReadonlyObservableValue<boolean> {
        return this.editMode;
    }

    public setEditMode(editMode: boolean): void {
        if (editMode !== this.editMode.value) {
            this.editMode.value = editMode;
        }
    }

    public getVersion(): GitVersionDescriptor {
        return this.version;
    }

    public setDiffOrientation(orientation: DiffViewerOrientation): void {
        if (this.diffOrientation.value !== orientation) {
            this.diffOrientation.value = orientation;

            const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
            vcViewModelDataProviderService.setUserPreferences({
                ...vcViewModelDataProviderService.getUserPreferences(),
                diffViewerOrientation: orientation
            });
        }
    }

    public getSelectedItem(): IReadonlyObservableValue<IGitItem> {
        return this.selectedItem;
    }

    public setSelectedItem(item: IGitItem, editMode?: boolean): void {
        if (item !== this.selectedItem.value) {
            this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").triggerNavigateAway(
                () => {
                    // Switch off edit mode when changing files unless requested to turn it on
                    if (!!editMode !== this.editMode.value) {
                        this.editMode.value = !!editMode;
                    }

                    this.updateSelectedItem(item);
                },
                () => {
                    // Set the selected item in order to allow components to react to the cancelled selection
                    this.selectedItem.value = this.selectedItem.value;
                }
            );
        } else if (editMode !== undefined && editMode !== this.editMode.value) {
            this.editMode.value = editMode;
        }
    }

    public setSelectedPath(path: string): void {
        path = this.fixPath(path);
        const item = this.getItem(path, false);
        if (item) {
            this.setSelectedItem(item);
        } else {
            const items = this.getChildItems(path);
            items.ready.subscribe(() => {
                const resolvedItem = this.getItem(path, true);
                this.setSelectedItem(resolvedItem);
            });
        }
    }

    public getLoadedFolders(): IReadonlyObservableArray<IFolderContents> {
        return this.loadedFolders;
    }

    public getChildItems(path: string, ensureLastChanges?: boolean, createIfNew?: boolean): IReadyableReadonlyObservableArray<IGitItem> {
        let childItems = this.itemsByParentPath[path];
        if (!childItems) {
            childItems = new ReadyableObservableArray<IGitItem>();
            this.itemsByParentPath[path] = childItems;

            const neededPaths: string[] = [path];

            // Resolve the parent paths of this path at the same time in order to be able to populate the source explorer tree
            let parentPath = getParentPath(path);
            while (parentPath) {
                if (this.itemsByParentPath[parentPath]) {
                    break;
                }
                neededPaths.push(parentPath);
                parentPath = getParentPath(parentPath);
            }

            getGitClient(this.pageContext)
                .getItemsBatch(
                    {
                        includeContentMetadata: false,
                        itemDescriptors: neededPaths.map(path => {
                            return {
                                path,
                                recursionLevel: VersionControlRecursionType.OneLevelPlusNestedEmptyFolders,
                                ...this.version
                            };
                        })
                    } as GitItemRequestData,
                    this.repository.id,
                    this.projectId
                )
                .then(
                    results => {
                        const itemsMap: { [path: string]: IInternalItemDescriptor[] } = {};
                        let lastInvalidItem: IInternalItemDescriptor | undefined;
                        for (let i = 0, l = neededPaths.length; i < l; i++) {
                            const itemPath = neededPaths[i];
                            let childItems = results[i] as IInternalItemDescriptor[];
                            let currentInvalidItem: IInternalItemDescriptor | undefined = undefined;
                            if (childItems.length === 0) {
                                // Item does not exist, create an invalid item entry
                                currentInvalidItem = {
                                    path: itemPath,
                                    isFolder: i > 0,
                                    isInvalid: true,
                                    isNewItem: createIfNew
                                };
                                childItems = [currentInvalidItem];
                            }
                            if (createIfNew && lastInvalidItem) {
                                // The last item is a non-existing "new" entry. Add it to its parent folder
                                childItems.push(lastInvalidItem);
                            }
                            lastInvalidItem = currentInvalidItem;
                            itemsMap[itemPath] = childItems;
                        }
                        this.processItems(itemsMap);

                        if (ensureLastChanges) {
                            this.ensureCommitDetailsFetched(path);
                        }
                    },
                    () => {
                        this.processItems({ [path]: [{ path, isFolder: false, commitId: "", objectId: "", isInvalid: true }] });
                    }
                );
        } else if (ensureLastChanges) {
            this.ensureCommitDetailsFetched(path);
        }

        return childItems;
    }

    public getCloneMenuItems(): IMenuItem[] {
        const menuItems: IMenuItem[] = [
            {
                groupKey: "header-items",
                iconProps: { iconName: "CloneToDesktop" },
                id: "clone",
                onActivate: this.getCommandExecutor("clone"),
                text: Resources.Clone
            }
        ];

        if (userHasClaim(this.pageContext, UserClaims.Member)) {
            menuItems.push(
                {
                    groupKey: "header-items",
                    iconProps: { iconName: "GitFork" },
                    id: "fork",
                    onActivate: this.getCommandExecutor("fork"),
                    text: Resources.Fork
                }
            )
        }

        return menuItems;
    }

    public getItemMenuItems(item: IGitItem, menuContributionIds?: string[]): IReadonlyObservableArray<IMenuItem> {
        const collection = new ObservableCollection<IMenuItem>();

        let canContribute = isFeatureFlagEnabled(this.pageContext, "WebAccess.VersionControl.Editing", true);
        if (canContribute) {
            const permissionService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");
            if (
                !permissionService.hasPermission(GitRepositoryPermissions.GenericContribute, this.version) &&
                !permissionService.hasPermission(GitRepositoryPermissions.CreateBranch)
            ) {
                canContribute = false;
            }
        }

        let hideEditAction = false;
        if (item.path === this.selectedItem.value.path) {
            hideEditAction = !!(
                this.selectedItemContent &&
                this.selectedItemContent.value &&
                (this.selectedItemContent.value.details.metadata.isBinary || this.selectedItemContent.value.details.contentTruncated)
            );
        }

        if (!item.isInvalid) {
            if (canContribute) {
                collection.push(
                    getItemCommonMenuItems(item.isFolder, item.path === "/", hideEditAction, commandName =>
                        this.getCommandExecutor(commandName, item)
                    )
                );
            }

            collection.push([
                {
                    groupKey: "item-download",
                    iconProps: { iconName: "Download" },
                    id: "download",
                    onActivate: () => {
                        window.open(this.getDownloadUrlForItem(item.path, this.version, item.isFolder, true), "_blank", "noopener");
                    },
                    text: item.isFolder ? Resources.DownloadAsZip : Resources.Download
                }
            ]);

            // Add contributed menu items
            if (menuContributionIds) {
                const contributionContext = {
                    gitRepository: this.repository,
                    item: {
                        sourceProvider: "Git",
                        path: item.path,
                        isFolder: item.isFolder,
                        isSymLink: item.isSymLink,
                        item: {
                            isFolder: item.isFolder,
                            serverItem: item.path,
                            version: this.versionString,
                            gitObjectType: item.isFolder ? GitObjectType.Tree : GitObjectType.Blob,
                            commitId: item.commitId
                                ? {
                                    full: item.commitId,
                                    short: item.commitId.substr(0, 8)
                                }
                                : undefined,
                            objectId: item.objectId
                                ? {
                                    full: item.objectId,
                                    short: item.objectId.substr(0, 8)
                                }
                                : undefined
                        },
                        url: this.getItemUrl(item.path)
                    },
                    version: this.version.version
                } as IFilesHubContributedMenuContext;
                contributionContext.getSourceItemContext = () => contributionContext;
                setRemoteSerializationSettings(contributionContext, { proxyFunctions: true });

                for (const menuContributionId of menuContributionIds) {
                    addMenuItems(this.pageContext, collection, menuContributionId, contributionContext);
                }
            }
        }

        return collection;
    }

    public getDiffOrientationMenuItem(): IHeaderCommandBarItem {
        const orientation = this.getDiffOrientation().value;
        const orientationLabel = orientation === DiffViewerOrientation.SideBySide ? Resources.SideBySideDiffTooltip : Resources.InlineDiffTooltip;

        return {
            ariaLabel: orientationLabel,
            iconProps: {
                iconName: orientation === DiffViewerOrientation.SideBySide ? "DiffSideBySide" : "DiffInline"
            },
            id: "diffOrientation",
            subMenuProps: {
                id: "diffOrientationSubMenu",
                items: [
                    {
                        iconProps: {
                            iconName: "DiffSideBySide"
                        },
                        id: "diffOrientationSideBySide",
                        onActivate: () => {
                            this.setDiffOrientation(DiffViewerOrientation.SideBySide);
                        },
                        text: Resources.SideBySideDiff
                    },
                    {
                        iconProps: {
                            iconName: "DiffInline"
                        },
                        id: "diffOrientationInline",
                        onActivate: () => {
                            this.setDiffOrientation(DiffViewerOrientation.Inline);
                        },
                        text: Resources.InlineDiff
                    }
                ]
            },
            subtle: true,
            text: orientation === DiffViewerOrientation.SideBySide ? Resources.SideBySideDiff : Resources.InlineDiff,
            tooltipProps: { text: orientationLabel }
        };
    }

    public getAllFiles(): IReadyableReadonlyObservableArray<IGitItem> {
        if (!this.allFilePaths) {
            this.allFilePaths = new ReadyableObservableArray<IGitItem>();
            getGitClient(this.pageContext)
                .getFilePaths(this.projectId, this.repository.id, undefined, this.version)
                .then(result => {
                    const gitItems: IGitItem[] = [];

                    const folders = new Set<string>();
                    for (const path of result.paths) {
                        const parentFolder = getParentPath(path);
                        if (parentFolder) {
                            if (!folders.has(parentFolder)) {
                                gitItems.push(this.createGitItem({ path: parentFolder, isFolder: true }));
                                folders.add(parentFolder);
                            }
                        }
                        gitItems.push(this.createGitItem({ path: "/" + path, isFolder: false }));
                    }

                    this.allFilePaths.value = gitItems;
                    this.allFilePaths.ready.value = true;
                });
        }
        return this.allFilePaths;
    }

    public getRootItem(): IGitItem {
        return this.createGitItem({ path: "/", isFolder: true });
    }

    public getSelectedItemContent(): IReadonlyObservableValue<IGitItemContent | undefined> {
        if (!this.selectedItemContent) {
            const contentObservable = new ObservableValue<IGitItemContent | undefined>(undefined);
            this.selectedItemContent = contentObservable;

            const selectedItem = this.selectedItem.value;
            if (selectedItem.isNewItem) {
                const name = getFileName(selectedItem.path);
                contentObservable.value = {
                    commitId: "",
                    details: {
                        content: "",
                        contentTruncated: false,
                        objectId: "",
                        metadata: {
                            contentType: "text/plain",
                            extension: getFileExtension(name),
                            fileName: getFileName(name)
                        } as FileContentMetadata,
                        resourceUri: getGitResourceUri(this.repository.id, this.version, selectedItem.path)
                    },
                    editContext: this.createFileEditContext(selectedItem, "")
                };
            } else if (!selectedItem.isFolder) {
                this.getItemContentPromise(selectedItem.path, this.version).then(content => {
                    if (!content.details.metadata.isBinary) {
                        content.editContext = this.createFileEditContext(selectedItem, content.details.content);
                    }
                    contentObservable.value = content;
                });
            }
        }
        return this.selectedItemContent;
    }

    public getSelectedItemAnnotationBlocks(): IReadyableReadonlyObservableArray<IAnnotationBlock> {
        if (!this.selectedItemAnnotationBlocks) {
            const path = this.selectedItem.value.path;
            this.selectedItemAnnotationBlocks = new ReadyableObservableArray<IAnnotationBlock>();

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

            getGitClient(this.pageContext)
                .getCommitsBatch(
                    {
                        $top: 1000,
                        excludeDeletes: true,
                        itemPath: path,
                        itemVersion: this.version
                    } as GitQueryCommitsCriteria,
                    this.repository.id,
                    this.projectId
                )
                .then(commits => {
                    // Make sure we are still on the same file
                    if (this.selectedItem.value.path === path) {
                        let refName: string | undefined;
                        if (this.version.versionType === GitVersionType.Branch) {
                            refName = branchNameToRef(this.version.version);
                        }

                        const routingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");

                        const history: IAnnotationHistoryEntry[] = commits.map((commit, index) => ({
                            annotatePreviousUrl:
                                commits.length > index + 1
                                    ? routingService.getFilesBlameUrl(
                                        this.repository.name,
                                        this.projectName,
                                        path,
                                        commitToVersionDescriptor(commits[index + 1].commitId)
                                    )
                                    : undefined,
                            authorName: commit.author.name,
                            authorEmail: commit.author.email,
                            changeUrl: routingService.getCommitUrl(this.repository.name, this.projectName, commit.commitId, refName, path),
                            comment: commit.comment,
                            date: commit.author.date,
                            shortId: getShortCommitId(commit.commitId)
                        }));

                        const getFileDiffs = (skip: number, top: number) => {
                            return contributionService.getDataAsync<IAnnotateBatchResult[]>(
                                annotateGitDiffsDataProviderId,
                                {
                                    path,
                                    repositoryId: this.repository.id,
                                    versions: commits.slice(skip, skip + top).map(commit => "GC" + commit.commitId)
                                },
                                true
                            );
                        };

                        loadAnnotationBlocks(this.selectedItem, this.selectedItemAnnotationBlocks!, history, getFileDiffs);
                    }
                });
        }
        return this.selectedItemAnnotationBlocks;
    }

    public getItemContent(path: string, version?: GitVersionDescriptor): IReadonlyObservableValue<IGitItemContent | undefined> {
        // Use selected item if it matches. This covers when the item is new and we should not try to fetch content to show Preview tab.
        if (
            this.selectedItemContent &&
            this.selectedItem &&
            this.selectedItem.value &&
            this.selectedItem.value.path === path &&
            (!version || this.versionString === getVersionString(version))
        ) {
            return this.selectedItemContent;
        }

        const contentObservable = new ObservableValue<IGitItemContent | undefined>(undefined);
        this.getItemContentPromise(path, version || this.version).then(content => {
            contentObservable.value = content;
        });
        return contentObservable;
    }

    public getFileDownloadUrl(path: string, version?: GitVersionDescriptor, forceDownload?: boolean): string {
        return this.getDownloadUrlForItem(path, version || this.version, false, !!forceDownload);
    }

    public getScrollToPosition(): { scrollLeft: number; scrollTop: number } | undefined {
        return this.scrollToPosition;
    }

    public getItemUrl(path: string, { version, absoluteUrl, tabId, anchor }: ItemUrlOptions = {}): string {
        const queryParams: IQueryParameter[] = [
            { name: "path", value: path },
            { name: "version", value: version || this.versionString }
        ];
        if (tabId) {
            queryParams.push({ name: "_a", value: tabId });
        }
        if (anchor) {
            queryParams.push({ name: "anchor", value: anchor });
        }

        const queryString = "?" + toQueryString(queryParams);
        let url = queryString;
        if (absoluteUrl) {
            url = window.location.origin + window.location.pathname + queryString;
        }

        return url;
    }

    public getExplorerCollapsed(): IReadonlyObservableValue<boolean> {
        return this.explorerCollapsed;
    }

    public setExplorerCollapsed(collapsed: boolean): void {
        this.explorerCollapsed.value = collapsed;
        trySetLocalStorageValue(explorerModeSettingsKey, collapsed ? "collapsed" : undefined);
    }

    public setScrollToPosition(position: { scrollLeft: number; scrollTop: number } | undefined): void {
        this.scrollToPosition = position;
    }

    public getUrlTransformer(path: string): IFilesHubUrlTransformer {
        return new MarkdownFilesHubUrlTransformer(
            path,
            false,
            versionString => versionString === this.versionString,
            (path, anchor) => this.getItemUrl(path, { anchor }),
            path => this.getDownloadUrlForItem(path, this.version, false, false)
        );
    }

    private updateSelectedItem(item: IGitItem) {
        this.selectedItemContent = undefined;
        this.selectedItemAnnotationBlocks = undefined;
        this.scrollToPosition = undefined;

        if (this.selectedItem.value.isNewItem) {
            // If we were on a new item, then perform an FPS to the new item in order to clear out
            // the new item entries.
            let newPath = item.path;

            // Pick the first non-new path to navigate to
            while (newPath && newPath !== "/") {
                const item = this.getItem(newPath, false);
                if (!(item && item.isNewItem)) {
                    break;
                }
                newPath = getParentPath(newPath)!;
            }

            FastPageSwitch(this.pageContext, this.getItemUrl(newPath), false);
        } else {
            this.selectedItem.value = item;
        }
    }

    private getItemContentPromise(path: string, version: GitVersionDescriptor): Promise<IGitItemContent> {
        const gitFilesService = this.pageContext.getService<IGitFilesService>("IGitFilesService");
        return gitFilesService.getItemContentPromise(path, version);
    }

    private getDownloadUrlForItem(path: string, version: GitVersionDescriptor, isFolder: boolean, forceDownload: boolean): string {
        const hostPath = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath;
        const url = new Uri(combineUrlPaths(hostPath, `${this.projectId}/_apis/git/repositories/${this.repository.id}/items`));

        url.addQueryParams({
            path: path,
            ["versionDescriptor[versionOptions]"]: version.versionOptions || 0,
            ["versionDescriptor[versionType]"]: version.versionType || 0,
            ["versionDescriptor[version]"]: version.version,
            resolveLfs: true,
            $format: isFolder ? "zip" : "octetStream",
            ["api-version"]: "5.0"
        });

        if (forceDownload) {
            url.addQueryParam("download", "true");
        }

        return url.absoluteUri;
    }

    private createFileEditContext(item: IGitItem, originalContent: string): FileEditContext {
        const context = new FileEditContext(
            originalContent,
            () => {
                showMessageDialog(this.pageContext, {
                    message: format(Resources.UnsavedFileFormat, item.path),
                    onClose: result => {
                        if (result) {
                            // Reset the content to its original value
                            context.setContent(originalContent);

                            // Turn off edit mode
                            this.setEditMode(false);

                            // Trigger selected-item-change event to allow
                            // components to re-render
                            this.updateSelectedItem(item);
                        }
                    },
                    okButtonProps: {
                        danger: true,
                        primary: false,
                        text: Resources.Discard
                    },
                    title: Resources.UnsavedChanges
                });
            },
            content => {
                this.executeFileActionCommand("commit", item, content);
            }
        );
        return context;
    }

    private getCommandExecutor(commandName: string, ...args: any[]): { (): void } {
        if (commandName === "edit") {
            return () => this.setSelectedItem(args[0], true);
        } else {
            return (): void => {
                this.executeFileActionCommand(commandName, ...args);
            };
        }
    }

    private executeFileActionCommand(commandName: string, ...args: any[]): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.executeCommandEx(
            "ms.vss-code-web.files-hub-actions-command",
            {
                dependencies: ["ms.vss-code-web.files-hub-actions-content"],
                serviceName: "IFilesHubActionsService",
                methodName: commandName
            },
            ...args
        );
    }

    private getItem(path: string, createIfNotFound: boolean): IGitItem {
        let item = this.itemsByPath[path];
        if (!item) {
            // Look for the item ignoring casing
            item = this.itemsByPathLowercase[path.toLocaleLowerCase()];

            if (!item && createIfNotFound) {
                // Item does not exist. Create a entry for the invalid item
                item = this.createGitItem({ path: path, isFolder: false, isInvalid: true });
            }
        }
        return item;
    }

    private processItems(items: { [path: string]: IInternalItemDescriptor[] }): void {
        const folderUpdates: IFolderContents[] = [];
        const childArrays: ReadyableObservableArray<IGitItem>[] = [];

        // Process all of our results
        for (const parentPath in items) {
            const result = this.processChildItems(parentPath, items[parentPath]);
            if (result) {
                childArrays.push(result);

                // Add true folder items to our list of folder updates to send
                const parentItem = this.getItem(parentPath, false);
                if (parentItem && parentItem.isFolder) {
                    folderUpdates.push({ path: parentPath, children: result.value });
                }
            }
        }

        // Send folder update events, ordered from items closest to the root to the longest path
        folderUpdates.sort((f1, f2) => f1.path.length - f2.path.length);
        this.loadedFolders.push(...folderUpdates);

        // Update the underlying readyable arrays
        for (const childArray of childArrays) {
            if (!childArray.ready.value) {
                childArray.ready.value = true;
            }
        }
    }

    private processChildItems(parentPath: string, childItems: IInternalItemDescriptor[]): ReadyableObservableArray<IGitItem> | undefined {
        const parentPathItems: IGitItem[] = [];
        const parentPathWithSlashEnd = parentPath[parentPath.length - 1] === "/" ? parentPath : parentPath + "/";

        for (const childItem of childItems) {
            const gitItem = this.createGitItem(childItem);

            // Don't process 2 cases:
            // 1. The first item in each data provider entry for the folder is the folder item itself.
            // 2. In some cases (e.g. Git submodules), deeper files/folders are returned, but they are not direct children.
            if (gitItem.path === parentPathWithSlashEnd + gitItem.name) {
                // Do a check for empty intermediate folders. For example, if
                // /Root contains a folder A which contains only a folder B but
                // no individual files, then we will see /Root/A and /Root/A/B in
                // the list of children here. Combine these into a single item with
                // the name "A/B".
                if (
                    gitItem.isFolder &&
                    parentPathItems.length &&
                    parentPathItems[parentPathItems.length - 1].isFolder &&
                    gitItem.path.startsWith(parentPathItems[parentPathItems.length - 1].path + "/")
                ) {
                    const emptyFolder = parentPathItems.pop()!;
                    parentPathItems.push({
                        ...gitItem,
                        name: gitItem.path.substr(parentPathWithSlashEnd.length),
                        lastCommit: emptyFolder.lastCommit
                    });
                } else {
                    parentPathItems.push(gitItem);
                }
            }
        }

        parentPathItems.sort(this.sortItems);

        let itemsObservableArray = this.itemsByParentPath[parentPath];
        if (!itemsObservableArray) {
            itemsObservableArray = new ReadyableObservableArray();
            this.itemsByParentPath[parentPath] = itemsObservableArray;
        }
        itemsObservableArray.value = parentPathItems;

        return itemsObservableArray;
    }

    private createGitItem(item: IInternalItemDescriptor): IGitItem {
        const existingItem = this.itemsByPath[item.path];
        if (existingItem) {
            return existingItem;
        }

        const name = getFileName(item.path);
        const gitItem = {
            commitId: item.commitId,
            iconProps:
                item.path === "/"
                    ? { iconName: "GitLogo" }
                    : item.isFolder
                        ? { iconName: "FabricFolderFill", className: "repos-folder-icon" }
                        : item.isSymLink
                            ? { iconName: "PageArrowRight" }
                            : { iconName: getIconNameForFile(name) },
            isInvalid: item.isInvalid,
            isFolder: item.isFolder,
            isNewItem: item.isNewItem,
            isSymLink: item.isSymLink,
            lastCommit: new ObservableValue(undefined),
            href: this.getItemUrl(item.path),
            name: item.path === "/" ? getGitRepository(this.pageContext)!.name : name,
            objectId: item.objectId,
            path: item.path
        };

        // Only cache the item if we know its commit id (or this is a new item the user is creating).
        // Otherwise it is a "fake" item we created where we only know the path.
        if (item.commitId || item.isNewItem) {
            this.itemsByPath[item.path] = gitItem;

            // Also cache the all-lowercase path so that we can find items with case insensitivity
            const lowerCasePath = item.path.toLocaleLowerCase();
            if (lowerCasePath !== item.path && !this.itemsByPathLowercase[lowerCasePath]) {
                this.itemsByPathLowercase[lowerCasePath] = gitItem;
            }
        }

        return gitItem;
    }

    private ensureCommitDetailsFetched(parentPath: string) {
        if (!this.foldersWithLastCommits[parentPath]) {
            this.foldersWithLastCommits[parentPath] = true;
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService
                .getDataAsync<ILastChangeDataProviderData>(lastChangeDataProviderId, {
                    allowPartial: true,
                    includeCommits: true,
                    path: parentPath,
                    repositoryId: this.repository.id,
                    version: this.versionString
                })
                .then(data => {
                    const commitsById: { [commitId: string]: GitCommitRef } = {};
                    if (data!.commits) {
                        for (const commit of data!.commits) {
                            commitsById[commit.commitId] = commit;
                        }
                    }

                    const reposRoutingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");

                    for (const item of data!.items) {
                        const gitItem = this.getItem(combineUrlPaths(parentPath, item.path), false);
                        const commit = commitsById[item.commitId];
                        if (gitItem && commit) {
                            gitItem.lastCommit.value = {
                                comment: getShortCommitComment(commit.comment),
                                date: commit.author.date,
                                commitId: commit.commitId,
                                name: commit.author.name,
                                href: reposRoutingService.getCommitUrl(this.repository.name, this.projectName, commit.commitId, this.refName),
                                shortCommitId: getShortCommitId(commit.commitId)
                            };
                        }
                    }
                });
        }
    }

    private fixPath(path: string): string {
        return "/" + trimTrailingSlash(trimLeadingSlash((path || "").replace(/\\/g, "/")));
    }

    private sortItems = (i1: IGitItem, i2: IGitItem): number => {
        return i1.isFolder === i2.isFolder ? localeIgnoreCaseComparer(i1.name, i2.name) : i1.isFolder ? -1 : 1;
    };
}

export function getFilesHubViewService(pageContext: IVssPageContext): IFilesHubViewService {
    return pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
}

Services.add("IFilesHubViewService", { serviceFactory: FilesHubViewService });

class FilesHubTabsService extends FilesHubTabsServiceBase {
    protected getTabRegistrations(): IFilesHubTabRegistration[] {
        return tabRegistrations;
    }

    protected getFilesHubService(pageContext: IVssPageContext) {
        return getFilesHubViewService(pageContext);
    }
}

const tabRegistrations: IFilesHubTabRegistration[] = [];

export function registerFilesHubTab(tabRegistration: IFilesHubTabRegistration): void {
    tabRegistrations.push(tabRegistration);
}

export function getFilesHubTabsService(pageContext: IVssPageContext): IFilesHubTabsService {
    return pageContext.getService<IFilesHubTabsService>("IFilesHubTabsService");
}

Services.add("IFilesHubTabsService", { serviceFactory: FilesHubTabsService });
